import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { isMsalConfigured } from "./msalConfig";

/* ═══════════════════════════════════════════════════════════════
   useUserContext — Custom hook that extracts the authenticated
   user's info from MSAL ID token claims.
   ═══════════════════════════════════════════════════════════════ */

/** Patriot Consulting's Entra tenant ID — used to flag internal staff */
export const PATRIOT_TENANT_ID = "6c3170a9-6ccb-495a-a35a-5a7606968598";

/** Parse the home tenant GUID out of an idp claim like
 *  "https://login.microsoftonline.com/{guid}/" or ".../v2.0".
 *  Returns "" if the URL isn't a Microsoft login authority or the
 *  path segment isn't a valid GUID. */
function parseHomeTenantFromIdp(idp) {
  if (typeof idp !== "string") return "";
  const match = idp.match(
    /^https:\/\/(?:login\.microsoftonline\.com|sts\.windows\.net)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i
  );
  return match ? match[1].toLowerCase() : "";
}

/**
 * @returns {{ user: UserContext | null, isLoading: boolean }}
 *
 * user shape:
 *   name       — display name (e.g. "James Metcalfe")
 *   email      — email / UPN (e.g. "james@acmebank.com")
 *   tenantId   — home Entra tenant ID (idTokenClaims.tid)
 *   objectId   — user's Entra object ID (idTokenClaims.oid)
 *   isInternal — true when the user belongs to the Patriot tenant
 */
export default function useUserContext() {
  /* When MSAL is not configured, return null user immediately */
  if (!isMsalConfigured) {
    return { user: null, isLoading: false };
  }

  return useUserContextInner();
}

/**
 * Inner hook — only called when MSAL is configured, so the
 * hooks are always invoked in the same order.
 */
function useUserContextInner() {
  const { accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const isLoading = inProgress !== "none";

  if (!isAuthenticated || accounts.length === 0) {
    return { user: null, isLoading };
  }

  const account = accounts[0];
  const claims = account.idTokenClaims || {};

  /* ── Internal employee detection ──
     B2B guests sign into the Patriot tenant (resource tenant), so tid
     is always the Patriot tenant ID for both members and guests.
     We need to distinguish members from guests:
       1. acct claim: 0 = member of the tenant, 1 = guest
       2. idp claim fallback: if the identity provider differs from the
          Patriot authority, the user federated in from another tenant (guest)
     Only Patriot employees (members, not guests) see the client picker. */
  const isGuest =
    claims.acct !== undefined
      ? claims.acct === 1
      : claims.idp
        ? claims.idp !== `https://login.microsoftonline.com/${PATRIOT_TENANT_ID}/v2.0`
        : false; // If neither claim is present, assume member (safe default for Patriot staff)

  /* For guests, tid is Patriot's tenant. Their home tenant GUID lives in
     the idp claim, e.g. "https://login.microsoftonline.com/{guid}/v2.0". */
  const homeTenantFromIdp = isGuest ? parseHomeTenantFromIdp(claims.idp) : "";
  const tenantId = homeTenantFromIdp || claims.tid || "";

  /* App roles from the ID token (Entra app role assignments). A "Demo.Only"
     role forces internal chrome off regardless of tenant/guest, so a demo
     account never sees the client picker / internal controls. */
  const roles = claims.roles || [];
  const isDemoOnly = roles.includes("Demo.Only");

  const user = {
    name: account.name || claims.name || "",
    email:
      account.username ||
      claims.preferred_username ||
      claims.email ||
      "",
    tenantId,
    objectId: claims.oid || "",
    isInternal: tenantId === PATRIOT_TENANT_ID && !isGuest && !isDemoOnly,
  };

  return { user, isLoading };
}
